import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {isAuthRedirectError} from '../../../shared/services/auth.service';
import {logError} from '../../../shared/services/logger.service';
import {uploadImage} from '../../../shared/services/image-service';
import {SavaquanHttpError} from '../../../shared/utils/http-error';
import {FieldPair} from '../../../shared/components/record/RecordKit';
import {usePermissions} from '../../../shared/permissions/PermissionContext';
import {PermissionCode} from '../../../shared/permissions/permissions';
import {EquipmentDto, SaveEquipmentDto} from '../dtos/equipment.dto';
import {createEquipment, deleteEquipment, getEquipment, getEquipmentCatalog, updateEquipment} from '../services/equipment.service';
import {
  brandsFor, brandStyle, CatalogModel, CUSTOM_CATEGORY, EquipmentCatalog, matchesModel, moneyInput, nicknamePlaceholder,
  plainNumber, stepped, typeLabel,
} from './equipment-catalog';
import {
  CardDivider, CatalogNote, CheckRow, ChoiceButton, ChoiceGrid, ConfirmDialog, EnterOwnButton, EquipmentPage, ErrorText, Field,
  LinkText, MoneyStepper, NameList, PageMessage, PickerCard, Pill, ProfileCard, Select, SerialNumberField, StepHeader, StepTitle,
  TitleWithPill,
} from './EquipmentKit';

/*
 * The equipment profile page in Android's design (EquipmentDetailScreen.kt and
 * EquipmentDetailViewModel.kt, SQ-441; the web page is SQ-592). A new machine
 * is picked from the catalog one step at a time (category, then type and
 * brand, then model, or typed in) and lands on its profile: nickname, year,
 * VIN, tracking number, and its price when it shows in estimates. An existing
 * machine opens on its profile, and "Pick brand and model" / "Change model"
 * walks the catalog again without losing what was typed. Nothing is saved
 * until Save, which sends the whole profile.
 */

/**
 * Where the page is. Brand, Model and Manual are one "Add New <category>"
 * card with the type at the top; Custom is the category step's "Not listed?
 * Add your own", a kind the catalog does not have.
 */
type Step = 'category' | 'brand' | 'model' | 'manual' | 'custom' | 'details';

interface Profile {
  category: string; type: string; brand: string; model: string; catalogId: string;
  nickname: string; year: string; serialNumber: string; trackingNumber: string;
  serialImageId?: number; serialPhotoUrl?: string;
  specs: Record<string, string>;
  showInEstimates: boolean; hourlyRate: string; minimumCharge: string;
}

const EMPTY: Profile = {
  category: '', type: '', brand: '', model: '', catalogId: '', nickname: '', year: '', serialNumber: '', trackingNumber: '',
  specs: {}, showInEstimates: true, hourlyRate: '', minimumCharge: '',
};

const fromDto = (e: EquipmentDto): Profile => ({
  category: e.category ?? '', type: e.type ?? '', brand: e.brand ?? '', model: e.model ?? '', catalogId: e.catalogId ?? '',
  nickname: e.nickname ?? '', year: e.year ? String(e.year) : '', serialNumber: e.serialNumber ?? '',
  trackingNumber: e.trackingNumber ?? '', serialImageId: e.serialImageId ?? undefined, serialPhotoUrl: e.serialPhotoUrl ?? undefined,
  specs: Object.fromEntries(Object.entries(e.specs ?? {}).map(([k, v]) => [k, v == null ? '' : String(v)])),
  showInEstimates: e.showInEstimates !== false,
  hourlyRate: e.hourlyRate != null ? plainNumber(e.hourlyRate) : '',
  minimumCharge: e.minimumCharge > 0 ? plainNumber(e.minimumCharge) : '',
});

const MODEL_NOT_LISTED = 'Model not listed? Tap here to enter it';
const MODEL_SEARCH = 'Search for your model';

/** The year dropdown: next year back to 1980, newest first. */
const thisYear = new Date().getFullYear();
const YEARS = Array.from({length: thisYear + 1 - 1980 + 1}, (_, i) => String(thisYear + 1 - i)).map(y => ({value: y, label: y}));

/**
 * The API takes any year, and Android or an import may have stored one outside
 * the list; it is offered too, in its place, or the select would read blank.
 */
const yearOptions = (year: string) =>
  !year || YEARS.some(y => y.value === year) ? YEARS
    : [...YEARS, {value: year, label: year}].sort((a, b) => Number(b.value) - Number(a.value));

const DELETE_NOTE =
  'It comes off the equipment list and the estimate picker. Estimates and jobs it is already on keep it and its price.';

const saveError = (e: unknown): string => {
  if (e instanceof SavaquanHttpError) {
    if (e.statusCode === 403) return 'Your access level cannot change equipment.';
    if (e.statusCode === 409) return 'Another piece of equipment already has this nickname.';
    if (e.statusCode === 400) {
      // The API's refusals are a plain sentence; anything else (a framework's JSON) is not for the reader.
      const text = e.details?.trim().replace(/^"|"$/g, '');
      return text && text.length < 200 && !text.startsWith('{') ? text : 'Check the details and try again.';
    }
  }
  return 'Not saved. Try again.';
};

const deleteError = (e: unknown): string =>
  e instanceof SavaquanHttpError && e.statusCode === 403 ? 'Your access level cannot delete equipment.' : 'Not deleted. Try again.';

export interface EquipmentProfilePageProps {
  /** Absent: a new machine, starting at the catalog's first step. */
  equipmentId?: number;
  onClose: () => void;
  /** Saved or deleted: the list reloads. */
  onChanged: () => void;
}

export const EquipmentProfilePage = ({equipmentId, onClose, onChanged}: EquipmentProfilePageProps) => {
  const navigate = useNavigate();
  const isNew = equipmentId === undefined;
  const {hasPermission} = usePermissions();
  const canSave = hasPermission(isNew ? PermissionCode.EquipmentCreate : PermissionCode.EquipmentEdit);
  const canDelete = !isNew && hasPermission(PermissionCode.EquipmentDelete);

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<string>();
  const [catalog, setCatalog] = useState<EquipmentCatalog>();
  const [step, setStep] = useState<Step>(isNew ? 'category' : 'details');
  const [error, setError] = useState<string>();
  const [working, setWorking] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // The picker's choices so far (not yet the profile's).
  const [pickCategory, setPickCategory] = useState<string>();
  const [pickType, setPickType] = useState<string>();
  const [pickBrand, setPickBrand] = useState<string>();
  const [modelQuery, setModelQuery] = useState('');
  const [manualBrand, setManualBrand] = useState('');
  const [manualModel, setManualModel] = useState('');
  const [customKind, setCustomKind] = useState('');

  // The profile. A machine saved before the catalog (no category) is finished here.
  const [needsSetup, setNeedsSetup] = useState(false);
  const [p, setProfile] = useState<Profile>(EMPTY);
  const set = (patch: Partial<Profile>) => setProfile(prev => ({...prev, ...patch}));
  const [serialUploading, setSerialUploading] = useState(false);
  const [serialPreview, setSerialPreview] = useState<string>();
  const upload = useRef(0);
  // A machine never priced (hourly_rate NULL) stays unpriced unless a rate is typed.
  const unpriced = useRef(isNew);

  const load = useCallback(() => {
    let active = true;
    setLoading(true);
    setLoadError(undefined);
    Promise.all([getEquipmentCatalog(navigate), isNew ? Promise.resolve(undefined) : getEquipment(navigate, equipmentId!)])
      .then(([c, machine]) => {
        if (!active) return;
        setCatalog(c);
        if (machine) {
          setProfile(fromDto(machine));
          unpriced.current = machine.hourlyRate == null;
          setNeedsSetup(!machine.category?.trim());
          setStep('details');
        }
      })
      .catch(e => {
        if (!active || isAuthRedirectError(e)) return;
        if (e instanceof SavaquanHttpError && e.statusCode === 403) setForbidden(true);
        else {
          logError('Error loading equipment', {equipmentId}, e);
          setLoadError(e instanceof SavaquanHttpError && e.statusCode === 404
            ? 'This equipment no longer exists.' : 'Could not load the equipment catalog.');
        }
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [navigate, equipmentId, isNew]);

  useEffect(load, [load]);

  useEffect(() => () => { if (serialPreview) URL.revokeObjectURL(serialPreview); }, [serialPreview]);

  // Every step starts at its top.
  const top = useRef<HTMLDivElement>(null);
  useEffect(() => { top.current?.scrollIntoView?.({block: 'nearest'}); }, [step]);

  const categoryDto = catalog?.categories.find(c => c.name === pickCategory);
  const brands = useMemo(() => categoryDto && pickType ? brandsFor(categoryDto, pickType) : [], [categoryDto, pickType]);
  const brandDto = brands.find(b => b.name === pickBrand);
  const shownModels = (brandDto?.models ?? []).filter(m => matchesModel(m, modelQuery));

  // ---- The picker -------------------------------------------------------------

  /**
   * Opens the category's card. A new machine starts with no type chosen;
   * Change model starts on the machine's own type.
   */
  const choosePickCategory = (name: string) => {
    const category = catalog?.categories.find(c => c.name === name);
    if (!category) return;
    setPickCategory(name);
    setPickType(category.types.find(t => t.name === p.type)?.name);
    setPickBrand(undefined);
    setManualBrand('');
    setManualModel('');
    setModelQuery('');
    setError(undefined);
    setStep('brand');
  };

  /** The type dropdown. A brand picked for another type goes; names being typed in right now stay. */
  const choosePickType = (name: string) => {
    if (name === pickType) return;
    const typing = step === 'manual';
    setPickType(name);
    setPickBrand(undefined);
    if (!typing) { setManualBrand(''); setManualModel(''); }
    setModelQuery('');
    setStep(typing ? 'manual' : 'brand');
  };

  const choosePickBrand = (name: string) => {
    setPickBrand(name);
    setModelQuery('');
    setStep('model');
  };

  const pickModel = (model: CatalogModel) => {
    if (!brandDto) return;
    set({category: pickCategory ?? '', type: model.type ?? pickType ?? '', brand: brandDto.name, model: model.name, catalogId: model.id ?? ''});
    setNeedsSetup(false);
    setStep('details');
  };

  /** "Enter my own brand and model": only the brand picked on this card's model list carries over. */
  const startManual = () => {
    setManualBrand(brandDto?.name ?? '');
    setManualModel('');
    setError(undefined);
    setStep('manual');
  };

  const finishManual = () => {
    if (!manualBrand.trim() || !manualModel.trim()) return setError('Enter the brand and the model.');
    set({category: pickCategory ?? '', type: pickType ?? '', brand: manualBrand.trim(), model: manualModel.trim(), catalogId: ''});
    setNeedsSetup(false);
    setError(undefined);
    setStep('details');
  };

  /** "Not listed? Add your own" under the categories: empty boxes, every time. */
  const startCustom = () => {
    setCustomKind('');
    setManualBrand('');
    setManualModel('');
    setError(undefined);
    setStep('custom');
  };

  /** The typed kind, brand and model become the profile; only the kind is required. */
  const finishCustom = () => {
    if (!customKind.trim()) return setError('Type what kind of equipment it is.');
    set({category: CUSTOM_CATEGORY, type: customKind.trim(), brand: manualBrand.trim(), model: manualModel.trim(), catalogId: ''});
    setPickCategory(undefined);
    setPickType(undefined);
    setPickBrand(undefined);
    setNeedsSetup(false);
    setError(undefined);
    setStep('details');
  };

  /** Details → the picker again: the machine's own category card when it has one, else the categories. */
  const changeModel = () => {
    if (catalog?.categories.some(c => c.name === p.category)) return choosePickCategory(p.category);
    setPickCategory(undefined);
    setPickType(undefined);
    setPickBrand(undefined);
    setError(undefined);
    setStep('category');
  };

  /** One step back inside the page; the page closes when there is nowhere to go back to. */
  const back = () => {
    let previous: Step | undefined;
    switch (step) {
      // A new machine's profile goes back to the list it was picked from.
      case 'details':
        previous = isNew ? (p.category === CUSTOM_CATEGORY ? 'custom' : !pickCategory ? undefined : p.catalogId ? 'model' : 'manual')
          : undefined;
        break;
      case 'category': previous = isNew ? undefined : 'details'; break;
      case 'custom': case 'brand': previous = 'category'; break;
      case 'model': case 'manual': previous = 'brand'; break;
    }
    if (!previous) return onClose();
    // Back to the brand buttons forgets the brand whose models were open.
    if (previous === 'brand') { setPickBrand(undefined); setModelQuery(''); }
    setError(undefined);
    setStep(previous);
  };

  // ---- The profile ------------------------------------------------------------

  const uploadSerialPhoto = (file: File) => {
    const mine = ++upload.current;
    setSerialPreview(URL.createObjectURL(file));
    setSerialUploading(true);
    setError(undefined);
    uploadImage(file)
      .then(stored => {
        if (upload.current !== mine) return;
        set({serialImageId: stored.id, serialPhotoUrl: stored.imageUrl});
      })
      .catch(e => {
        if (upload.current !== mine) return;
        logError('Error uploading the serial plate picture', {equipmentId}, e);
        setSerialPreview(undefined);
        setError('The picture did not upload. Try again.');
      })
      .finally(() => upload.current === mine && setSerialUploading(false));
  };

  const removeSerialPhoto = () => {
    upload.current++;
    setSerialUploading(false);
    setSerialPreview(undefined);
    set({serialImageId: undefined, serialPhotoUrl: undefined});
  };

  const save = async () => {
    if (working || serialUploading) return;
    const problem =
      isNew && p.category !== CUSTOM_CATEGORY && (!p.brand.trim() || !p.model.trim()) ? 'Choose a brand and model first.'
        : !p.type.trim() ? 'Choose what kind of equipment this is.'
          : p.year && (p.year.length !== 4 || Number(p.year) < 1900 || Number(p.year) > 2100) ? 'Enter the year as four digits, like 2024.'
            : undefined;
    if (problem) return setError(problem);
    const body: SaveEquipmentDto = {
      nickname: p.nickname.trim(),
      type: p.type,
      category: p.category,
      brand: p.brand.trim(),
      model: p.model.trim(),
      catalogId: p.catalogId,
      year: Number(p.year) || 0,
      serialNumber: p.serialNumber.trim(),
      trackingNumber: p.trackingNumber.trim(),
      serialImageId: p.serialImageId ?? 0,
      specs: Object.fromEntries(Object.entries(p.specs).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()])),
      showInEstimates: p.showInEstimates,
      // Absent leaves the rate as it is: NULL stays NULL. Clearing a rate that was set sends 0.
      hourlyRate: p.hourlyRate === '' && unpriced.current ? undefined : Number(p.hourlyRate) || 0,
      minimumCharge: Number(p.minimumCharge) || 0,
    };
    setWorking(true);
    setError(undefined);
    try {
      if (isNew) await createEquipment(navigate, body);
      else await updateEquipment(navigate, equipmentId!, body);
      onChanged();
      onClose();
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error saving equipment', {equipmentId}, e as Error);
      setError(saveError(e));
      setWorking(false);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    if (working || isNew) return;
    setWorking(true);
    setError(undefined);
    try {
      await deleteEquipment(navigate, equipmentId!);
      onChanged();
      onClose();
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error deleting equipment', {equipmentId}, e as Error);
      setError(deleteError(e));
      setWorking(false);
    }
  };

  const title = step !== 'details' && isNew ? 'Add Equipment'
    : isNew ? `New ${typeLabel(p.type) ?? 'Equipment'}`
      : p.nickname.trim() || `${p.brand} ${p.model}`.trim() || p.type.trim() || 'Equipment';

  // ---- The steps --------------------------------------------------------------

  // Until the catalog is in, the page carries a title; after that every step's card has its own heading.
  if (loading || forbidden || !catalog) {
    return (
      <EquipmentPage>
        <h1 className="eq-title">{title}</h1>
        {loading ? <PageMessage>Loading…</PageMessage>
          : forbidden ? <PageMessage>Your access level does not include the equipment list.</PageMessage>
            : <div>
              <ErrorText>{loadError ?? 'Could not load the equipment catalog.'}</ErrorText>
              <LinkText label="Retry" onClick={load}/>
            </div>}
      </EquipmentPage>
    );
  }

  const categoryStep = (
    <PickerCard>
      <StepHeader title="What kind of equipment?" action="Cancel" onAction={back}/>
      <ChoiceGrid>
        {catalog.categories.map(c => (
          <ChoiceButton key={c.name} radio label={c.name} selected={c.name === p.category}
                        onClick={() => choosePickCategory(c.name)}/>
        ))}
      </ChoiceGrid>
      {/* A kind the catalog lacks: typed in on the next card, never a new category here. */}
      <EnterOwnButton label="Not listed? Add your own" onClick={startCustom}/>
    </PickerCard>
  );

  const customStep = (
    <PickerCard>
      <StepHeader title="Add your own" onAction={back}/>
      <Field floating="Category" value={customKind} placeholder="What is it? (required)" onChange={setCustomKind}/>
      <Field floating="Brand" value={manualBrand} placeholder="Optional" onChange={setManualBrand}/>
      <Field floating="Model" value={manualModel} placeholder="Optional" onChange={setManualModel}/>
      {error && <ErrorText>{error}</ErrorText>}
      <Pill wide label="Continue" onClick={finishCustom}/>
    </PickerCard>
  );

  const brandPart = <>
    <EnterOwnButton label="Enter my own brand and model" onClick={startManual}/>
    <StepTitle>Choose a brand</StepTitle>
    {brands.length === 0
      ? <PageMessage>No brands listed for this one yet. Enter your own above.</PageMessage>
      : <>
        <ChoiceGrid>
          {brands.map(b => (
            <ChoiceButton key={b.name} label={b.label ?? b.name} style={brandStyle(catalog.brandColors?.[b.name])}
                          onClick={() => choosePickBrand(b.name)}/>
          ))}
        </ChoiceGrid>
        <CatalogNote/>
      </>}
  </>;

  const modelPart = <>
    <TitleWithPill title={`${brandDto?.label ?? pickBrand ?? ''} · Choose a model`} pill="Change brand" onPill={back}/>
    <EnterOwnButton label={MODEL_NOT_LISTED} onClick={startManual}/>
    <Field value={modelQuery} placeholder={MODEL_SEARCH} onChange={setModelQuery}/>
    <div className="eq-count">{shownModels.length === 1 ? '1 model' : `${shownModels.length} models`} · match the name on your machine</div>
    <NameList names={shownModels.map(m => m.name)} onPick={i => pickModel(shownModels[i])}
              empty={modelQuery.trim() ? 'No matching models.' : 'No models listed for this brand yet.'}/>
    <CatalogNote/>
    {/* The same way out again at the foot of a long list. */}
    <LinkText label={MODEL_NOT_LISTED} onClick={startManual}/>
  </>;

  const manualPart = <>
    <TitleWithPill title="Enter your own" pill="Pick from the list" onPill={back}/>
    <Field floating="Brand" value={manualBrand} placeholder="Required" onChange={setManualBrand}/>
    <Field floating="Model" value={manualModel} placeholder="Required" onChange={setManualModel}/>
    {error && <ErrorText>{error}</ErrorText>}
    <Pill wide label="Continue" onClick={finishManual}/>
  </>;

  const pickCard = categoryDto && (
    <PickerCard>
      <StepHeader title={`${isNew ? 'Add New ' : ''}${categoryDto.name}`} onAction={back}/>
      <Select title="Type" value={pickType} placeholder="Choose a type" onChange={choosePickType}
              options={categoryDto.types.map(t => ({value: t.name, label: typeLabel(t.label ?? t.name)!}))}/>
      {/* Nothing under the dropdown until a type is picked: the brands and the way to type your own both depend on it. */}
      {pickType && (step === 'manual' ? manualPart : step === 'model' && brandDto ? modelPart : brandPart)}
    </PickerCard>
  );

  const details = <>
    <ProfileCard>
      <div className="eq-row eq-head">
        {/* The type is the heading; the brand and model sit under it, smaller. */}
        <div>
          <div className="eq-head-title">{typeLabel(p.type) ?? 'Type not set'}</div>
          <div className="eq-head-sub">{`${p.brand} ${p.model}`.trim() || 'Brand and model not set'}</div>
        </div>
        <div className="eq-head-pills">
          {!isNew && !needsSetup && canSave && <Pill compact tone="tonal" label="Change model" onClick={changeModel}/>}
          <Pill compact tone="tonal" label="Back" onClick={back}/>
        </div>
      </div>
      {needsSetup && (
        <div className="eq-setup">
          <span className="eq-status">Finish setup</span>
          This machine was added before the equipment catalog. Pick its category, brand and model to finish setting it
          up. It stays on your estimates meanwhile.
          <Pill compact label="Pick brand and model" onClick={changeModel}/>
        </div>
      )}
      <CardDivider/>
      <FieldPair>
        <Field heading="Nickname" optional value={p.nickname} placeholder={nicknamePlaceholder(p.category, p.type)}
               onChange={nickname => set({nickname})}/>
        <Select compact title="Year" optional value={p.year} placeholder="Choose" options={yearOptions(p.year)} onChange={year => set({year})}/>
      </FieldPair>
      <FieldPair>
        <SerialNumberField heading="VIN / serial number" value={p.serialNumber} onChange={serialNumber => set({serialNumber})}
                           photoUrl={serialPreview ?? p.serialPhotoUrl} uploading={serialUploading}
                           onPhoto={uploadSerialPhoto} onRemovePhoto={removeSerialPhoto}/>
        <Field heading="Tracking number" optional value={p.trackingNumber} placeholder="The number supplied with your tracker"
               onChange={trackingNumber => set({trackingNumber})}/>
      </FieldPair>
      <CardDivider/>
      {/* The pricing opens only when the machine shows in estimates; turning it off keeps the rates. */}
      <div className={p.showInEstimates ? 'eq-show eq-show--on' : 'eq-show'}>
        <CheckRow label="Show in estimates" checked={p.showInEstimates} onChange={showInEstimates => set({showInEstimates})}/>
        <div className="eq-note">
          {p.showInEstimates
            ? 'This machine shows up in the equipment list on every work item (trees, stumps, etc.).'
            : 'Turn this on to price this machine and add it to work items (trees, stumps, etc.). Turning it off never removes it from past jobs it was added to.'}
        </div>
        {p.showInEstimates && <>
          <CardDivider/>
          <div>
            <div className="eq-card-heading">Equipment pricing</div>
            <div className="eq-card-note">Use $0 if this machine is included in your crew rate.</div>
          </div>
          <div className="eq-steppers">
            <MoneyStepper title="Hourly rate" value={p.hourlyRate}
                          onChange={v => set({hourlyRate: moneyInput(v)})}
                          onStep={up => setProfile(prev => ({...prev, hourlyRate: stepped(prev.hourlyRate, up)}))}/>
            <MoneyStepper title="Minimum per job" value={p.minimumCharge}
                          onChange={v => set({minimumCharge: moneyInput(v)})}
                          onStep={up => setProfile(prev => ({...prev, minimumCharge: stepped(prev.minimumCharge, up)}))}/>
          </div>
          <div className="eq-note">
            The minimum is the least this machine bills on one job. When its time on a job comes to less, the job total
            adds the difference; each work item still shows the price of its own time.
          </div>
        </>}
      </div>
    </ProfileCard>
    {error && <ErrorText>{error}</ErrorText>}
    {working ? <div className="eq-working"><span className="eq-spinner" aria-label="Saving"/></div> : (
      <div className="eq-actions">
        {canSave && <Pill wide label={isNew ? 'Save Equipment' : 'Save Changes'} onClick={() => void save()} disabled={serialUploading}/>}
        {canDelete && <Pill wide tone="danger" label="Delete Equipment" onClick={() => setConfirmDelete(true)}/>}
      </div>
    )}
    {confirmDelete && (
      <ConfirmDialog title={`Delete ${title}?`} body={DELETE_NOTE} cancel="Keep it" confirm="Delete equipment"
                     onCancel={() => setConfirmDelete(false)} onConfirm={() => void remove()}/>
    )}
  </>;

  return (
    <EquipmentPage ref={top}>
      {step === 'category' && categoryStep}
      {step === 'custom' && customStep}
      {(step === 'brand' || step === 'model' || step === 'manual') && pickCard}
      {step === 'details' && details}
    </EquipmentPage>
  );
};

export default EquipmentProfilePage;
